import React, { FC, useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import classnames from 'classnames';

import {
  ReservationFlowSelectedPaymentMethodNewCreditCard,
  ReservationFlowMe,
  ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo,
} from 'src/interfaces/reservation_flow';
import {
  canTransitFromMainToGettingStripeCreditCardTokenOnMain,
  canTransitFromMainToCompleteWithMulpayCreditCardToken,
  canTransitFromMainToCompleteOnMain,
} from 'src/state-machines/purchase/product-purchase-machine';
import { ProductPurchaseServiceContext } from 'src/contexts/state-machines/purchase/ProductPurchaseServiceContext';
import { useIsMobile } from 'src/reservation-page/hooks/helpers/useIsMobile';
import { RSDetailHeader } from 'src/reservation-page/detail-header/RSDetailHeader';
import { RSDetailHeaderButton } from 'src/reservation-page/detail-header/RSDetailHeaderButton';

import { ProductSummary } from 'src/organisms/product-purchase/ProductSummary';

import { CustomerInfoFieldset } from './components/CustomerInfoFieldset';
import { PaymentMethodFieldset } from './components/PaymentMethodFieldset';

import styles from '../../index.module.css';

export interface FormValues {
  customerInfo?: { [key: string]: string };
  paymentMethod?:
    | ReservationFlowSelectedPaymentMethodCreditCardHoldingNewCreditCardInfo
    | ReservationFlowSelectedPaymentMethodNewCreditCard;
}

export interface MainViewProps {
  onRequirePrev: () => void;
  user: ReservationFlowMe;
}

export const MainView: FC<MainViewProps> = ({ onRequirePrev, user }) => {
  const intl = useIntl();

  const isMobile = useIsMobile();

  const [state, send] = ProductPurchaseServiceContext.useActor();

  const dataProductDetail = ProductPurchaseServiceContext.useSelector(
    (state) => state.context.data.productDetail!,
  );

  const input = ProductPurchaseServiceContext.useSelector(
    (state) => state.context.input,
  );

  const formMethods = useForm<FormValues>({
    mode: 'onBlur',
  });

  const { handleSubmit, reset } = formMethods;

  const onFormSubmit = useCallback<SubmitHandler<FormValues>>(() => {
    send({
      type: 'CONFIRM',
    });
  }, [send]);

  const isFormValueInitializedRef = useRef(false);
  useEffect(() => {
    if (isFormValueInitializedRef.current) return;

    const {
      customerInfo: inputCustomerInfo,
      paymentMethod: inputPaymentMethod,
    } = input;

    // useFormのdefaultValuesを用いると未選択状態(undefinedをセット)にした際に、defaultValueの値が優先されるためresetを利用する
    reset({
      customerInfo: (() => {
        // 入力値がない場合はdataCustomerFieldsのデフォルト値を設定する
        if (typeof inputCustomerInfo === 'undefined') {
          return dataProductDetail.customer_fields.reduce((acc, current) => {
            if (current.default_value === null) {
              return acc;
            }

            return {
              ...acc,
              [`c_${current.id}`]: current.default_value,
            };
          }, {});
        }

        return inputCustomerInfo.reduce((acc, current) => {
          return {
            ...acc,
            [`c_${current.id}`]: current.value,
          };
        }, {});
      })(),
      paymentMethod: inputPaymentMethod,
    });
    isFormValueInitializedRef.current = true;
  }, [dataProductDetail, input, reset]);

  return (
    <FormProvider {...formMethods}>
      <form
        id="ticketBookPurchaseForm"
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          handleSubmit(onFormSubmit)();
        }}
      >
        <div className="z-5 sticky top-0">
          <RSDetailHeader
            title={intl.formatMessage({
              defaultMessage: '回数券購入',
              id: 'tsnt7S',
              description:
                'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/MainView/index.tsx',
            })}
            leftButton={
              <RSDetailHeaderButton
                variant="close"
                type="button"
                onClick={onRequirePrev}
              >
                {intl.formatMessage({
                  defaultMessage: '戻る',
                  id: 'bWPs71',
                  description:
                    'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/MainView/index.tsx',
                })}
              </RSDetailHeaderButton>
            }
            rightButton={
              <RSDetailHeaderButton
                variant="ok"
                form="ticketBookPurchaseForm"
                disabled={
                  !canTransitFromMainToGettingStripeCreditCardTokenOnMain(
                    state.context,
                  ) &&
                  !canTransitFromMainToCompleteWithMulpayCreditCardToken(
                    state.context,
                  ) &&
                  !canTransitFromMainToCompleteOnMain(state.context)
                }
              >
                {intl.formatMessage({
                  defaultMessage: '次へ',
                  id: 'fRGlVN',
                  description:
                    'src/templates/merchants/resources/book/components/product-purchase-modal/TicketBookPurchaseModal/components/MainView/index.tsx',
                })}
              </RSDetailHeaderButton>
            }
          />
        </div>

        <div
          className={classnames(
            'overflow-y-auto',
            isMobile
              ? styles.mobileHeightRestriction
              : styles.pcHeightRestriction,
          )}
        >
          <div className="pc:px-4 pc:pt-4 border-b border-l-0 border-r-0 border-t-0 border-solid border-gray-50 px-2.5 pb-2.5 pt-3">
            <ProductSummary productDetail={dataProductDetail} />
          </div>
          <div className="pc:px-4 pc:pb-4 space-y-5 px-2.5 pb-6 pt-2.5">
            {dataProductDetail.customer_fields.length > 0 && (
              <CustomerInfoFieldset />
            )}
            <PaymentMethodFieldset user={user} />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
